/**
 * LengthSliders Component
 * Configures content length settings for different content types
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Ruler, HelpCircle, FileText, Layers } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { LengthConfig, ContentLength, DescriptionLength, SectionDepth } from '@/hooks/useContentConfig';

interface LengthSlidersProps {
  value: LengthConfig;
  onChange: (value: LengthConfig) => void;
  sourceLabel?: string;
}

const FAQ_LENGTH_OPTIONS: { value: ContentLength; label: string; description: string }[] = [
  { value: 'brief', label: 'Brief', description: '1-2 sentences' },
  { value: 'standard', label: 'Standard', description: '3-5 sentences' },
  { value: 'detailed', label: 'Detailed', description: '1-2 paragraphs' },
];

const DESCRIPTION_LENGTH_OPTIONS: { value: DescriptionLength; label: string; description: string }[] = [
  { value: 'concise', label: 'Concise', description: '50-100 words' },
  { value: 'standard', label: 'Standard', description: '100-200 words' },
  { value: 'comprehensive', label: 'Comprehensive', description: '200-400 words' },
];

const SECTION_DEPTH_OPTIONS: { value: SectionDepth; label: string; description: string }[] = [
  { value: 'shallow', label: 'Shallow', description: '2-3 key points' },
  { value: 'standard', label: 'Standard', description: '4-6 key points' },
  { value: 'deep', label: 'Deep', description: '7+ comprehensive sections' },
];

function lengthToNumber(value: ContentLength | DescriptionLength | SectionDepth): number {
  const map: Record<string, number> = {
    brief: 0, concise: 0, shallow: 0,
    standard: 1,
    detailed: 2, comprehensive: 2, deep: 2,
  };
  return map[value] ?? 1;
}

function numberToFaqLength(n: number): ContentLength {
  return (['brief', 'standard', 'detailed'] as ContentLength[])[n] ?? 'standard';
}

function numberToDescriptionLength(n: number): DescriptionLength {
  return (['concise', 'standard', 'comprehensive'] as DescriptionLength[])[n] ?? 'standard';
}

function numberToSectionDepth(n: number): SectionDepth {
  return (['shallow', 'standard', 'deep'] as SectionDepth[])[n] ?? 'standard';
}

export function LengthSliders({ value, onChange, sourceLabel }: LengthSlidersProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Ruler className="h-5 w-5 text-green-500" />
            <CardTitle className="text-lg">Content Length</CardTitle>
          </div>
          {sourceLabel && (
            <Badge variant="outline" className="text-xs">
              From: {sourceLabel}
            </Badge>
          )}
        </div>
        <CardDescription>
          Control how detailed different types of content should be
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* FAQ Length */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-medium">FAQ Answer Length</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="secondary" className="text-xs">
                    {FAQ_LENGTH_OPTIONS[lengthToNumber(value.faqLength)].label}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {FAQ_LENGTH_OPTIONS[lengthToNumber(value.faqLength)].description}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Slider
            value={[lengthToNumber(value.faqLength)]}
            onValueChange={([n]) => onChange({ ...value, faqLength: numberToFaqLength(n) })}
            max={2}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            {FAQ_LENGTH_OPTIONS.map((opt) => (
              <span key={opt.value}>{opt.label}</span>
            ))}
          </div>
        </div>

        {/* Description Length */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-medium">Product Description Length</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="secondary" className="text-xs">
                    {DESCRIPTION_LENGTH_OPTIONS[lengthToNumber(value.descriptionLength)].label}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {DESCRIPTION_LENGTH_OPTIONS[lengthToNumber(value.descriptionLength)].description}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Slider
            value={[lengthToNumber(value.descriptionLength)]}
            onValueChange={([n]) => onChange({ ...value, descriptionLength: numberToDescriptionLength(n) })}
            max={2}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            {DESCRIPTION_LENGTH_OPTIONS.map((opt) => (
              <span key={opt.value}>{opt.label}</span>
            ))}
          </div>
        </div>

        {/* Section Depth */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-medium">AEO Section Depth</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="secondary" className="text-xs">
                    {SECTION_DEPTH_OPTIONS[lengthToNumber(value.sectionDepth)].label}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {SECTION_DEPTH_OPTIONS[lengthToNumber(value.sectionDepth)].description}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Slider
            value={[lengthToNumber(value.sectionDepth)]}
            onValueChange={([n]) => onChange({ ...value, sectionDepth: numberToSectionDepth(n) })}
            max={2}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            {SECTION_DEPTH_OPTIONS.map((opt) => (
              <span key={opt.value}>{opt.label}</span>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
